import { useCallback, useContext, useEffect, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import SearchIcon from '@mui/icons-material/Search'
import { Context } from '../../../context/AuthContext'
import PermissionComponent, { usePode } from '../../../permissions'
import { PERMISSOES } from '../../../const/permissoes'
import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import ConfirmDialog from '../../Shared/ConfirmDialog'
import ExportMenu from '../../Shared/ExportMenu'
import LoadingBackdrop from '../../Shared/LoadingBackdrop'
import SelectSubCompany from '../../Shared/SelectSubCompany'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import {
  createProductAssociationService,
  deleteProductAssociationService,
  editProductAssociationService,
  getProductAll,
  getProductAssociationSearchService,
  getProductAssociationService,
  getUniqueProductAssociationService,
} from '../../../services/productService'
import { mensagemDeErro } from '../../../services/apiClient'
import { getRfidSituation } from '../../../const/productionStatus'
import { formatDate, formatDateTime } from '../../../utils/format'
import useDebounce from '../../../utils/useDebounce'

const emptyForm = {
  productId: '',
  productName: '',
  epc: '',
  barcode: '',
  lot: '',
  expiresAt: '',
}

const ProductAssociation = () => {
  const { subCompany } = useContext(Context)
  const { notify, FeedbackSnackbar } = useFeedback()
  const podeGerenciar = usePode(PERMISSOES.RFID_GERENCIAR)

  const [subCompanyId, setSubCompanyId] = useState(
    subCompany.length === 1 ? subCompany[0].subCompanyId : ''
  )
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [products, setProducts] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [editing, setEditing] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  useDebounce(
    () => {
      setSearchTerm(search)
      setPage(0)
    },
    500,
    [search]
  )

  const fetchData = useCallback(async () => {
    if (!subCompanyId) {
      setRows([])
      setTotal(0)
      return
    }
    setLoading(true)
    try {
      const offset = page * rowsPerPage
      const response = searchTerm
        ? await getProductAssociationSearchService(rowsPerPage, offset, subCompanyId, searchTerm)
        : await getProductAssociationService(rowsPerPage, offset, subCompanyId)
      setRows(response.data.result || [])
      setTotal(response.data.total || 0)
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível carregar as associações.'), 'error')
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [subCompanyId, searchTerm, rowsPerPage, page, notify])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Export leva todo o filtro, não só a página: pagina a API em lotes.
  const fetchAllForExport = useCallback(async () => {
    if (!subCompanyId) return []
    const BLOCO = 200
    const todas = []
    let offset = 0
    for (;;) {
      const response = searchTerm
        ? await getProductAssociationSearchService(BLOCO, offset, subCompanyId, searchTerm)
        : await getProductAssociationService(BLOCO, offset, subCompanyId)
      const itens = response.data.result || []
      todas.push(...itens)
      offset += BLOCO
      if (offset >= (response.data.total || 0) || itens.length === 0) break
    }
    return todas
  }, [subCompanyId, searchTerm])

  const loadProducts = useCallback(async () => {
    try {
      const response = await getProductAll({ limit: 200, offset: 0 })
      setProducts(response.data.result || [])
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível carregar a lista de produtos.'), 'error')
    }
  }, [notify])

  const updateForm = (field, value) => setForm(current => ({ ...current, [field]: value }))

  // `products` traz só a primeira página: ao editar, o produto associado pode
  // estar fora dela, então o próprio item serve de opção selecionada.
  const selectedProduct =
    products.find(item => item.id === form.productId) ||
    (form.productId ? { id: form.productId, name: form.productName } : null)

  const handleCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setDialogOpen(true)
    loadProducts()
  }

  const handleEdit = async rfidItemId => {
    setSaving(true)
    try {
      const response = await getUniqueProductAssociationService(rfidItemId)
      const association = response.data
      setForm({
        productId: association.productId || '',
        productName: association.productName || '',
        epc: association.epc || '',
        barcode: association.barcode || '',
        lot: association.lot || '',
        expiresAt: association.expiresAt ? association.expiresAt.substring(0, 10) : '',
      })
      setEditing(association)
      setDialogOpen(true)
      loadProducts()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível carregar a associação selecionada.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    if (!form.productId) {
      notify('Selecione o produto da etiqueta.', 'warning')
      return
    }
    if (!form.epc.trim()) {
      notify('Informe o EPC da etiqueta.', 'warning')
      return
    }
    // A API só aceita estes campos: `epc` é imutável e não vai no PATCH.
    const payload = {
      productId: form.productId,
      lot: form.lot.trim() || null,
      barcode: form.barcode.trim() || null,
      expiresAt: form.expiresAt || null,
    }
    setSaving(true)
    try {
      if (editing) {
        await editProductAssociationService({ ...payload, rfidItemId: editing.id })
        notify('Associação atualizada com sucesso!')
      } else {
        await createProductAssociationService({
          ...payload,
          epc: form.epc.trim(),
          subCompanyId,
        })
        notify('Associação criada com sucesso!')
      }
      setDialogOpen(false)
      fetchData()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível salvar a associação.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setSaving(true)
    try {
      await deleteProductAssociationService(deleteTarget)
      notify('Associação excluída com sucesso!')
      setDeleteTarget(null)
      fetchData()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível excluir a associação.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const dataColumns = [
    { id: 'epc', label: 'EPC', element: row => row.epc || '—' },
    { id: 'productName', label: 'Nome do produto', element: row => row.productName || '—' },
    { id: 'barcode', label: 'Código de barras', element: row => row.barcode || '—' },
    { id: 'lot', label: 'Lote', element: row => row.lot || '—' },
    {
      id: 'situation',
      label: 'Situação',
      element: row => (
        <Chip
          size="small"
          label={row.situationLabel || '—'}
          color={getRfidSituation(row.situation).color}
        />
      ),
    },
    {
      id: 'expiresAt',
      label: 'Data de validade',
      element: row => formatDate(row.expiresAt),
    },
    { id: 'printCount', label: 'Impressões', align: 'right', element: row => row.printCount ?? '—' },
    { id: 'created', label: 'Criado', element: row => formatDateTime(row.createdAt) },
  ]

  const columns = [
    {
      id: 'actions',
      label: 'Ações',
      width: 110,
      element: row => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={podeGerenciar ? 'Editar' : 'Sem permissão para editar associações'}>
            <span>
              <IconButton size="small" disabled={!podeGerenciar} onClick={() => handleEdit(row.id)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={podeGerenciar ? 'Excluir' : 'Sem permissão para excluir associações'}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={!podeGerenciar}
                onClick={() => setDeleteTarget(row.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
    ...dataColumns,
  ]

  const exportColumns = [
    { header: 'EPC', accessor: 'epc' },
    { header: 'Nome do produto', accessor: 'productName' },
    { header: 'Código de barras', accessor: 'barcode' },
    { header: 'Lote', accessor: 'lot' },
    { header: 'Situação', accessor: 'situationLabel' },
    { header: 'Data de validade', accessor: row => formatDate(row.expiresAt) },
    { header: 'Impressões', accessor: 'printCount' },
    { header: 'Criado', accessor: row => formatDateTime(row.createdAt) },
  ]

  return (
    <Box>
      <PageHeader
        title="Associação de Produtos"
        subtitle="Vínculo entre produtos e etiquetas RFID"
        actions={
          <>
            <ExportMenu
              rows={rows}
              resolveRows={fetchAllForExport}
              columns={exportColumns}
              fileName="associacao-produtos"
              title="Associação de Produtos"
              disabled={total === 0}
            />
            <PermissionComponent permissao={PERMISSOES.RFID_GERENCIAR}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!subCompanyId}
                onClick={handleCreate}
              >
                Criar
              </Button>
            </PermissionComponent>
          </>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <SelectSubCompany
              value={subCompanyId}
              onChange={event => {
                setSubCompanyId(event.target.value)
                setPage(0)
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Pesquisar"
              value={search}
              disabled={!subCompanyId}
              onChange={event => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyMessage={
          subCompanyId ? 'Nenhum registro encontrado.' : 'Selecione uma filial para começar.'
        }
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        rowKey={row => row.id}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setRowsPerPage(parseInt(event.target.value, 10))
          setPage(0)
        }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? 'Editar associação' : 'Nova associação'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={products}
                value={selectedProduct}
                getOptionLabel={option => option.name || ''}
                isOptionEqualToValue={(option, selected) => option.id === selected.id}
                noOptionsText="Nenhum produto encontrado"
                onChange={(event, newValue) =>
                  setForm(current => ({
                    ...current,
                    productId: newValue ? newValue.id : '',
                    productName: newValue ? newValue.name : '',
                  }))
                }
                renderInput={params => <TextField {...params} label="Produto" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="EPC"
                value={form.epc}
                disabled={Boolean(editing)}
                helperText={editing ? 'O EPC de uma etiqueta já associada não pode ser alterado.' : ''}
                onChange={event => updateForm('epc', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Código de barras"
                value={form.barcode}
                onChange={event => updateForm('barcode', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Lote"
                value={form.lot}
                onChange={event => updateForm('lot', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Data de validade"
                InputLabelProps={{ shrink: true }}
                value={form.expiresAt}
                onChange={event => updateForm('expiresAt', event.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setDialogOpen(false)}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Excluir associação"
        description="Tem certeza que deseja excluir essa associação?"
        confirmLabel="Excluir"
        loading={saving}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <LoadingBackdrop open={saving} message="Processando..." />
      <FeedbackSnackbar />
    </Box>
  )
}

export default ProductAssociation
